import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Search, StopCircle, RefreshCw, Users, Phone, Mail,
  CheckCircle, Clock, Zap, BarChart3, AlertTriangle
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const fmtDate = (d) => { try { return d ? format(parseISO(d), 'MMM d, h:mm a') : '-'; } catch { return '-'; } };
const fmtNum  = (n) => n != null ? Number(n).toLocaleString() : '0';

export default function ContactEnrichment() {
  const [status,   setStatus]   = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/contacts/enrich/status');
      setStatus(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Auto-poll while running
  useEffect(() => {
    if (status?.is_running) {
      pollRef.current = setInterval(fetchStatus, 5000);
    } else {
      if (pollRef.current) clearInterval(pollRef.current);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [status?.is_running, fetchStatus]);

  const startEnrichment = async () => {
    setStarting(true);
    try {
      const res = await api.post('/api/contacts/enrich/start');
      toast.success(res.data.message);
      setTimeout(fetchStatus, 1000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    } finally { setStarting(false); }
  };

  const stopEnrichment = async () => {
    setStopping(true);
    try {
      await api.post('/api/contacts/enrich/stop');
      toast.info('Stop requested — finishing current batch...');
      setTimeout(fetchStatus, 2000);
    } catch { toast.error('Failed to stop'); }
    finally { setStopping(false); }
  };

  const job   = status?.job;
  const stats = status?.stats || {};
  const isRunning = status?.is_running;
  const pct  = job?.progress_pct || 0;
  const totalRE = stats.total_re_brokers || 0;
  const enrichedPct = totalRE > 0 ? Math.round(((stats.has_phone || 0) / totalRE) * 100) : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Search size={26} className="text-primary" />
            Contact Enrichment
          </h1>
          <p className="page-subtitle">
            Perplexity searches the web for phone numbers &amp; emails for all RE Broker contacts missing contact info
          </p>
        </div>
        <Button onClick={fetchStatus} variant="outline" size="sm">
          <RefreshCw size={13} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Live status banner */}
      {isRunning && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20 fade-in">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-primary">Enrichment running…</p>
            <p className="text-xs text-muted-foreground">
              Processing: <strong>{job?.current_company || '...'}</strong>
              {job?.processed > 0 && ` · ${fmtNum(job.processed)} agents checked · ${fmtNum(job.enriched)} contacts found`}
            </p>
          </div>
          <Button onClick={stopEnrichment} disabled={stopping} variant="outline" size="sm" className="flex-shrink-0">
            <StopCircle size={13} className="mr-1.5 text-red-500" />
            {stopping ? 'Stopping...' : 'Stop'}
          </Button>
        </div>
      )}

      {/* Stats grid */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-2 mb-1"><Users size={14} className="text-primary" /></div>
            <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{fmtNum(totalRE)}</div>
            <div className="text-xs text-muted-foreground">Total RE Brokers</div>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="flex items-center gap-2 mb-1"><Phone size={14} className="text-green-600" /></div>
            <div className="text-2xl font-bold tabular-nums text-green-700" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{fmtNum(stats.has_phone)}</div>
            <div className="text-xs text-muted-foreground">Have Phone</div>
          </Card>
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-center gap-2 mb-1"><Mail size={14} className="text-blue-600" /></div>
            <div className="text-2xl font-bold tabular-nums text-blue-700" style={{ fontFamily: 'Cormorant Garamond,serif' }}>{fmtNum(stats.has_email)}</div>
            <div className="text-xs text-muted-foreground">Have Email</div>
          </Card>
          <Card className={`p-4 ${stats.missing_contact_info > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
            <div className="flex items-center gap-2 mb-1"><AlertTriangle size={14} className={stats.missing_contact_info > 0 ? 'text-amber-600' : 'text-green-600'} /></div>
            <div className={`text-2xl font-bold tabular-nums ${stats.missing_contact_info > 0 ? 'text-amber-700' : 'text-green-700'}`} style={{ fontFamily: 'Cormorant Garamond,serif' }}>
              {fmtNum(stats.missing_contact_info)}
            </div>
            <div className="text-xs text-muted-foreground">Still Missing</div>
          </Card>
        </div>
      )}

      {/* Coverage progress bar */}
      {!loading && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
              <BarChart3 size={15} className="text-primary" />
              Contact Coverage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Phone numbers found</span>
                <span className="font-semibold">{enrichedPct}% ({fmtNum(stats.has_phone)} / {fmtNum(totalRE)})</span>
              </div>
              <Progress value={enrichedPct} className="h-2.5" />
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Email addresses found</span>
                <span className="font-semibold">{totalRE > 0 ? Math.round((stats.has_email / totalRE) * 100) : 0}% ({fmtNum(stats.has_email)} / {fmtNum(totalRE)})</span>
              </div>
              <Progress value={totalRE > 0 ? (stats.has_email / totalRE) * 100 : 0} className="h-2.5" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active job progress */}
      {job && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
                {isRunning ? 'Enrichment in Progress' : `Last Job — ${job.status}`}
              </CardTitle>
              <Badge variant="secondary" className={`text-xs ${
                job.status === 'running' ? 'bg-primary/10 text-primary' :
                job.status === 'completed' ? 'bg-green-100 text-green-700' :
                'bg-muted text-muted-foreground'
              }`}>
                {job.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progress bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {isRunning ? `Scanning: ${job.current_company || '...'}` : 'Completed'}
                </span>
                <span className="font-semibold tabular-nums">{pct}%</span>
              </div>
              <Progress value={pct} className="h-3" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{fmtNum(job.processed)} agents checked</span>
                <span>{fmtNum(job.total)} total</span>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-xl font-bold text-green-600 tabular-nums">{fmtNum(job.enriched)}</div>
                <div className="text-xs text-muted-foreground">Contacts Found</div>
              </div>
              <div>
                <div className="text-xl font-bold tabular-nums">{fmtNum(job.processed)}</div>
                <div className="text-xs text-muted-foreground">Processed</div>
              </div>
              <div>
                <div className="text-xl font-bold text-muted-foreground tabular-nums">
                  {job.processed > 0 ? Math.round((job.enriched / job.processed) * 100) : 0}%
                </div>
                <div className="text-xs text-muted-foreground">Hit Rate</div>
              </div>
            </div>

            <div className="flex gap-2 text-xs text-muted-foreground">
              {job.created_at && <span><Clock size={10} className="inline mr-1" />Started: {fmtDate(job.created_at)}</span>}
              {job.completed_at && <span className="ml-auto"><CheckCircle size={10} className="inline mr-1 text-green-500" />Done: {fmtDate(job.completed_at)}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Start / description */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Cormorant Garamond,serif' }}>
            How it works
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { icon: Users, title: 'Reads your CRM', desc: `Finds all ${fmtNum(stats.missing_contact_info)} RE Brokers missing phone or email` },
              { icon: Search, title: 'Perplexity searches', desc: 'Looks up each agent on Realtor.com, Zillow, company sites & LinkedIn in batches of 10' },
              { icon: CheckCircle, title: 'Auto-updates CRM', desc: 'Found numbers and emails are saved directly to each contact record' },
            ].map((step, i) => (
              <div key={i} className="flex gap-3 p-3 rounded-lg bg-muted/40">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <step.icon size={15} className="text-primary" />
                </div>
                <div>
                  <div className="text-sm font-semibold">{step.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{step.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle size={13} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              <strong>Note:</strong> This job runs continuously in the background (~30-60 min for all agents). 
              You can use the rest of the CRM while it runs. It only finds info that's publicly available online.
            </p>
          </div>

          <div className="flex gap-3">
            {!isRunning ? (
              <Button
                onClick={startEnrichment}
                disabled={starting || stats.missing_contact_info === 0}
                className="bg-primary flex-1"
                data-testid="start-enrichment-btn"
              >
                {starting ? (
                  <span className="flex items-center gap-2"><div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />Starting...</span>
                ) : stats.missing_contact_info === 0 ? (
                  <span className="flex items-center gap-2"><CheckCircle size={14} />All contacts enriched!</span>
                ) : (
                  <span className="flex items-center gap-2"><Zap size={14} />Start Enrichment ({fmtNum(stats.missing_contact_info)} agents)</span>
                )}
              </Button>
            ) : (
              <Button onClick={stopEnrichment} disabled={stopping} variant="destructive" className="flex-1">
                <StopCircle size={14} className="mr-1.5" />
                {stopping ? 'Stopping...' : 'Stop Enrichment'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
